import { type MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useMemo } from 'react';
import UserRole from '../../../../shared/AuthRoles';
import { capitalizeName } from '../../../../shared/utils';
import GenericTable, { LinkType } from '../../../shared/GenericTable';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';
import { MRT_Cell } from 'mantine-react-table';
import { useSelector } from 'react-redux';
import { selectTableReservations } from '../../../../app/Slices/TableReservationsSlice';
import { ReportType } from '../../../pages/Reports Page/IndividualReportPage';
import { trackEvent } from '../../../../app/Analytics';

export const capitalizedCell = <TData extends Record<string, any>>({
    cell,
}: {
    cell: MRT_Cell<TData>;
}): string => {
    const value = cell.getValue<string>();
    return value ? capitalizeName(value) : '';
};

export const formattedDateCell = <TData extends Record<string, any>>({
    cell,
}: {
    cell: MRT_Cell<TData>;
}): string => {
    const value = cell.getValue();
    return value ? moment(value).format('DD/MM/YYYY') : '';
};

export default function ReportList(params: any) {
    const loggedInUser = UserRole();
    const allReservations = useSelector(selectTableReservations);
    const processedData = useMemo(() => {
        if (!params.data) return [];
        return params.data.map((row: any) => ({
            ...row,
            startDate: new Date(new Date(row.startDate).setHours(0, 0, 0, 0)),
            endDate: new Date(new Date(row.endDate).setHours(0, 0, 0, 0)),
            cancelledDate: new Date(
                new Date(row.cancelledDate).setHours(0, 0, 0, 0),
            ),
        }));
    }, [params.data]);

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'moduleName',
            header: 'Experience',
            size: 240,
        },
        {
            accessorKey: 'ownerName',
            header: 'Host',
            size: 240,
        },
        {
            accessorKey: 'moduleType',
            header: 'Type',
            filterVariant: 'select',
            size: 180,
            mantineFilterSelectProps: {
                data: [
                    { label: 'Property', value: 'property' },
                    { label: 'Commercial', value: 'commercial' },
                    { label: 'Boat', value: 'boat' },
                    { label: 'Activity', value: 'activity' },
                    { label: 'Vehicle', value: 'vehicle' },
                ],
                clearable: true,
            },

            Cell: capitalizedCell,
        },
        {
            accessorKey: 'id',
            header: 'Reservation Ref',
            Cell: ({ cell }) => `#${cell.getValue<number>()}`,
            filterFn: (row, columnId, filterValue) => {
                const value = row.getValue<number>(columnId);
                return value?.toString().includes(filterValue);
            },
        },
        {
            accessorKey: 'guestName',
            header: loggedInUser?.role.includes('owner')
                ? 'Company'
                : 'Reserved By',
            accessorFn: (row) =>
                loggedInUser?.role.includes('owner')
                    ? row?.guestName
                        ? capitalizeName(row.guestName)
                        : 'On Behalf'
                    : capitalizeName(row?.guestName || row?.bookedBy),
            Cell: ({ cell }) => cell.getValue<string>() ?? '',
        },
        {
            accessorKey: 'bookedFor',
            header: loggedInUser?.role.includes('owner')
                ? 'Primary Guest'
                : 'Guest',
            Cell: capitalizedCell,
        },
        {
            accessorKey: 'bookerType',
            header: 'Guest Type',
            size: 120,
            Cell: capitalizedCell,
        },
        {
            accessorKey: 'visitType',
            header: 'Type of Visit',
            filterVariant: 'select',
            mantineFilterSelectProps: {
                data: [
                    { label: 'Business', value: 'business' },
                    { label: 'Personal', value: 'personal' },
                ],
                clearable: true,
            },
            Cell: ({ row, cell }) =>
                cell.getValue() === null
                    ? 'Blocked'
                    : capitalizeName(cell.getValue<string>()),
        },
        {
            accessorKey: 'startDate',
            header: 'Reservation From',
            filterVariant: 'date',
            filterFn: 'greaterThanOrEqualTo',
            sortingFn: 'datetime',
            mantineFilterDateInputProps: {
                valueFormat: 'DD/MM/YYYY',
            },
            Cell: formattedDateCell,
        },
        {
            accessorKey: 'endDate',
            header: 'Reservation To',
            filterVariant: 'date',
            filterFn: 'lessThanOrEqualTo',
            sortingFn: 'datetime',
            mantineFilterDateInputProps: {
                valueFormat: 'DD/MM/YYYY',
            },
            Cell: formattedDateCell,
        },
        {
            accessorKey: 'guests',
            header: 'Guests',
            filterFn: 'betweenInclusive',
            size: 120,
        },
        {
            accessorKey: 'overnightGuests',
            header: 'Overnight Guests',
            filterFn: 'betweenInclusive',
            size: 120,
            Cell: ({ row, cell }) => {
                const value = cell.getValue<number | null>();
                return value != null ? value : '-';
            },
        },
        {
            accessorKey: 'requiresCatering',
            header: 'Catering',
            filterVariant: 'select',
            filterFn: (row, _columnId, filterValue) => {
                if (filterValue === '') return true;
                return String(row.getValue('requiresCatering')) === filterValue;
            },
            mantineFilterSelectProps: {
                data: [
                    { label: 'Yes', value: 'true' },
                    { label: 'No', value: 'false' },
                ],
                clearable: true,
            },
            Cell: ({ row, cell }) => {
                const value = cell.getValue();
                let displayValue = '-';
                if (value === true) displayValue = 'Yes';
                else if (value === false) displayValue = 'No';

                return displayValue;
            },
        },
        {
            accessorKey: 'requiresBeverages',
            header: 'Beverages',
            filterVariant: 'select',
            filterFn: (row, _columnId, filterValue) => {
                if (filterValue === '') return true; // no filter applied
                return (
                    String(row.getValue('requiresBeverages')) === filterValue
                );
            },
            mantineFilterSelectProps: {
                data: [
                    { label: 'Yes', value: 'true' },
                    { label: 'No', value: 'false' },
                ],
                clearable: true,
            },
            Cell: ({ row, cell }) => {
                const value = cell.getValue();
                let displayValue = '-';
                if (value === true) displayValue = 'Yes';
                else if (value === false) displayValue = 'No';

                return displayValue;
            },
        },

        {
            accessorKey: 'status',
            header: 'Status',
            filterVariant: 'select',
            filterFn: (row, _columnId, filterValue) => {
                if (!filterValue) return true;
                return row.getValue('status') === filterValue;
            },
            mantineFilterSelectProps: {
                data: [
                    { label: 'Pending', value: 'pending' },
                    { label: 'Cancelled', value: 'cancelled' },
                    { label: 'Approved', value: 'approved' },
                    { label: 'Declined', value: 'declined' },
                    { label: 'Completed', value: 'completed' },
                ],
                placeholder: 'Filter by status',
                clearable: true,
            },
            Cell: ({ row, cell }) => {
                const status = cell.getValue<string>();
                return (
                    <CustomChip
                        label={capitalizeName(status)}
                        color={
                            status === 'completed'
                                ? 'green'
                                : status === 'cancelled'
                                  ? 'red'
                                  : status === 'approved'
                                    ? 'yellow'
                                    : status === 'declined'
                                      ? 'gray'
                                      : 'blue'
                        }
                        cursor='pointer'
                        variant='outlined'
                    />
                );
            },
        },
        {
            accessorKey: 'cancelledDate',
            header: 'Cancelled',
            filterVariant: 'date',
            filterFn: 'equals',
            sortingFn: 'datetime',
            mantineFilterDateInputProps: {
                valueFormat: 'DD/MM/YYYY',
            },
            Cell: formattedDateCell,
        },
        {
            accessorKey: 'cancelledBy',
            header: 'Cancelled By',
            Cell: capitalizedCell,
        },
        {
            accessorKey: 'cancellationReason',
            header: 'Cancellation Reason',
            Cell: capitalizedCell,
        },
        {
            accessorKey: 'declinedDate',
            header: 'Declined',
            filterVariant: 'date',
            filterFn: 'equals',
            sortingFn: 'datetime',
            mantineFilterDateInputProps: {
                valueFormat: 'DD/MM/YYYY',
            },
            Cell: formattedDateCell,
        },
        {
            accessorKey: 'declinedBy',
            header: 'Declined By',
            Cell: capitalizedCell,
        },
        {
            accessorKey: 'declineReason',
            header: 'Declined Reason',
            Cell: capitalizedCell,
        },
    ];

    const onRowClick = () => {
        trackEvent('Reports', 'FY Report Clicked', `FY Report Clicked`);
    };

    const reportUrl: Record<ReportType, string> = {
        [ReportType.FY]: 'reports/fyReport',
        [ReportType.Monthly]: 'reports/monthlyReport',
        [ReportType.Quarterly]: 'reports/quarterlyReport',
        [ReportType.YTD]: 'reports/ytdReport',
        [ReportType.Reservations]: 'reports/reservationsReport',
        [ReportType.Cancellations]: 'reports/cancellationReport',
        [ReportType.Declined]: 'reports/declinedReport',
    };

    const reportType = params.reportType as ReportType;

    const linkState = {
        name: 'viewReservation',
        component: 'reports',
        link: reportUrl[reportType],
        reservations: allReservations,
    };

    return (
        <GenericTable
            columns={columns}
            data={processedData}
            tableName='7DAY-Reservations-Report'
            loading={!params.data}
            columnVisibility={params?.columnVisibility}
            linkType={LinkType.Reservation}
            linkState={linkState}
            onRowClick={onRowClick}
        />
    );
}
